import React, { useEffect, useContext, useRef, useImperativeHandle, forwardRef } from 'react';
import { Chart } from 'chart.js/auto';
import axios from 'axios';
import style from '../css/Report.module.css';
import { TokenContext } from "./TokenContext";

const ReportCard = forwardRef(({ data, selectedIndexes, toggleSelection, startDate, endDate }, ref) => {
  const chartRefs = useRef([]); // canvas 참조
  const chartInstances = useRef([]); // Chart.js 인스턴스 저장
  const { bearerToken, _csrf } = useContext(TokenContext); // Context 사용

  //  useImperativeHandle을 사용하여 부모에서 호출할 수 있는 함수 정의
  useImperativeHandle(ref, () => ({
    sendHTMLToServer,
  }));

  // 데이터 정리를 위한 함수
  const processChartData = (testResult, resultType) => {
    if (!testResult) {
      return null;
    }

    let stepCountKey, studentStepsKey, stepIdKey, stepNumKey, scoreKey;

    switch (resultType) {
      case "word":
        stepCountKey = "word_step_count";
        studentStepsKey = "student_word_steps";
        stepIdKey = "word_step_id";
        stepNumKey = "word_step_num";
        scoreKey = "step_test_score";
        break;
      case "sentence":
        stepCountKey = "sentence_step_count";
        studentStepsKey = "student_sentence_steps";
        stepIdKey = "sentence_step_id";
        stepNumKey = "sentence_step_num";
        scoreKey = "step_test_score";
        break;
      case "test":
        stepCountKey = "test_step_count";
        studentStepsKey = "student_test_steps";
        stepIdKey = "test_step_id";
        stepNumKey = "test_step_num";
        scoreKey = "step_test_score";
        break;
      default:
        // return { labels: [], datasets: [] };
        // 지원하지 않는 result_type의 경우 무시
        return null;
    }

    const stepCount = testResult[stepCountKey];
    const studentSteps = testResult[studentStepsKey];

    // studentSteps == null 이어도, 표를 그려야 한다. 
    // test_score 값을 모두 0으로 설정하도록 한다. 
    if (stepCount) {
      const mergedData = stepCount.map((step) => {
        const matchingStudentStep = studentSteps?.find(
          (studentStep) => studentStep[stepIdKey] === step[stepIdKey]
        );
        return {
          step_num: step[stepNumKey],
          test_score: matchingStudentStep ? matchingStudentStep[scoreKey] : 0,
        };
      });

      return {
        labels: mergedData.map((item) => item.step_num),
        datasets: [
          {
            label: "학습 데이터",
            data: mergedData.map((item) => item.test_score),
            backgroundColor: "rgba(75, 192, 192, 0.2)",
            borderColor: "rgba(75, 192, 192, 1)",
            borderWidth: 1,
          },
        ],
      };

    } else {
      if (!stepCount) {
        // ('==> stepCount (NULL)')
      }
      if (!studentSteps) {
        // ('==> studentSteps (NULL)')
      }
      return null;
    }
  };

  // HTML 생성 및 저장
  const createHTMLTemplate = () => {
    const filteredData = data.filter((_, index) => selectedIndexes.includes(index));  // 체크된 항목만 필터링

    return `<!DOCTYPE html>
<html lang="en">
<head>
    <meta charset="UTF-8">
    <meta name="viewport" content="width=device-width, initial-scale=1.0">
    <title>성적표</title>
    <script src="https://cdn.jsdelivr.net/npm/chart.js"></script>
    <style>
        body {
            font-family: Arial, sans-serif;
            margin: 0 10mm 10mm 10mm;
        }
        header {
            display: flex;
            flex-direction: column;
            align-items: center;
            margin-bottom: 0;
        }
        header .header-content {
            display: flex;
            justify-content: space-between;
            align-items: center;
            width: 100%;
            padding: 0 0;
            margin-bottom: 0;
            box-sizing: border-box;
        }
        header .logo {
            max-width: 100%;
            height: auto;
            max-height: 92px;
            width: auto;
            min-width: 20%;
        }
        header h1 {
            font-size: 2rem;
            margin: 0;
        }
        header .subtitle {
            font-size: 1rem;
            color: #555;
        }
        header .name-label {
            display: flex;
            align-items: center;
            text-align: right;
            flex: 0;
            gap: 5px;
        }
        header .name-input {
            width: 100px;
            border-bottom: 2px solid black;
            display: inline-block;
            text-align: center;
            padding-bottom: 3px;
            font-size: 14px;
        }        
        main {
            margin-top: 40px; /* Head 와의 여백 추가 */
            margin-bottom: 40px; /* Footer와의 여백 추가 */
        }
        .footer {
            background-color: #fff;
            display: grid;
            grid-template-columns: 1fr auto 1fr;
            align-items: center;
            padding: 30px 20px;
            font-size: 1.1rem;
            border-top: 4px solid #000;
            text-align: center;
        }
        .footer div:nth-child(2) {
            text-align: center;
            color: red;
        }
        .footer div:nth-child(1) {
            text-align: left;
        }
        .footer div:nth-child(3) {
            text-align: right;
        }
        .header-label-container {
            display: flex;
            align-items: center;
            font-size: 1.2rem;
            font-weight: bold;
            margin-top: 20px;
            margin-bottom: 10px;
        }
        .header-label {
            color: #fff;
            background-color: #4CAF50;
            padding: 2px 10px;
            border-radius: 3px;
            margin-right: 10px;
        }
        .header-info {
            color: #333;
            font-size: 1rem;
        }
        .type-label {
            color: #333;
            font-size: 0.9rem;
            margin-left: 20px;
            color: #999999;

        }
        .working-date {
            color: #333;
            font-size: 0.9rem;
            margin-left: 20px;
            color: #909090;  
        }
        .total-label {
            font-size: 1.2rem;
            font-weight: bold;
            margin-top: 20px;
            margin-bottom: 10px;
            width: 100%;
            text-align: center;
        }
        .header-variable {
            color: gray;
            margin-left: 5px;
        }
        .study-period {
            color: red;
            font-size: 1.5rem;
            font-weight: bold;
            float: right;
            margin-top: -20px; /* 제목과 정렬 맞추기 위한 마진 */
        }
        table {
            width: 100%;
            border-collapse: collapse;
            margin-bottom: 20px;
        }
        table, th, td {
            border: 1px solid #ccc;
        }
        th, td {
            padding: 10px;
            text-align: center;
        }
        th {
            background-color: #f4f4f4;
        }
        .chart-container {
            width: 100%; /* 그래프 폭을 표와 동일하게 설정 */
            margin: 0 auto;
        }
        canvas {
            display: block;
            max-width: 100%;
        }

        /* canvas {
            max-width: 600px;
            margin: 0 auto;
            display: block;
        } */
    </style>
</head>
<body>
    <!-- Header -->
    <header>
        {{titleHtml}}
    </header>

    <!-- Main Content -->
    <main>

        <!-- <h1>학습 결과</h1> -->
        <span class="study-period">학습기간: ${startDate} ~ ${endDate}</span>
        <div style={{ height: '20px', width: '100%' }}>&nbsp</div>

        ${filteredData
        .map((item, index) => {

          let isChartData = true;
          const chartData = processChartData(item.test_result, item.result_type);
          if (!chartData) {
            // item.test_result === null 이면, 테이블만 표시한다.
            // if (item.result_type === "word" || item.result_type === "sentence" || item.result_type === "test") {
            //   // 무시 처리
            //   return "";
            // }
            isChartData = false;
          }

          const resultType = item.result_type;  // "word", "sentence", "test"
          let totalItemCount = 0, totalSteps = 0, completedSteps = 0, progressRate = 0;
          if (resultType === "word" || resultType === "sentence" || resultType === "test") {
            const stepCountKey = `${resultType}_step_count`;
            const studentStepsKey = `student_${resultType}_steps`;

            // result_type === "test"인 경우 키가 다름
            const poolCountKey = resultType === "test" ? "test_step_item_count" : `${resultType}_step_pool_count`;

            if (item.test_result) {

              // 총 항목 개수 계산
              totalItemCount = item.test_result[stepCountKey]?.reduce(
                (acc, step) => acc + (step[poolCountKey] || 0), 0
              ) || 0;

              // 총 차시 개수 계산
              totalSteps = item.test_result[stepCountKey]?.length || 0;

              // 진행 차시 개수 계산
              completedSteps = item.test_result[studentStepsKey]?.length || 0;

              // 진행율(%) 계산 (소수점 반올림)
              progressRate = totalSteps > 0 ? Math.round((completedSteps / totalSteps) * 100) : 0;
            }
            // =========================
          }

          const typeLabel = resultType === "word" ? "단어"
            : resultType === "sentence" ? "문장"
              : resultType === "test" ? "문제"
                : resultType === "topic" ? "권"
                  : resultType === "ebook" ? "권"
                    : "";

          const typeLabel2 = resultType === "word" ? "단어"
            : resultType === "sentence" ? "문장"
              : resultType === "test" ? "문제집"
                : resultType === "topic" ? "토픽"
                  : resultType === "ebook" ? "Ebook"
                    : "";

          // const lastDate = formatToKoreanTime(item.last_date);
          const latestDate = getLatestDate(item);
          const formattedLatestDate = latestDate ? formatToKoreanTime(latestDate) : "00/00 00:00";

          return `
            <section>
                <!-- <h2>${item.title}</h2> -->

                <div class="header-label-container">
                    <span class="header-label">${typeLabel2}</span>
                    <span class="header-info">
                        ${item.title}
                        ${(resultType === "word" || resultType === "sentence" || resultType === "test") ? (`
                          <span class="type-label">${totalItemCount} ${typeLabel}</span>      
                          `) : ''}
                        <span class="working-date">${formattedLatestDate}</span>
                        <!--
                        리딩튜터 입문 - Section 04 Unit 02
                        <span class="header-variable">20 단어</span>
                        <span class="header-variable">2025-08-04</span>
                        <span class="header-variable">13:51</span>
                        -->
                    </span>
                </div>

                ${(resultType === "word" || resultType === "sentence" || resultType === "test") ? (`
                <table>
                    <thead>
                        <tr>
                          
                          ${resultType === "word" ? (`
                              <th>스피킹</th>
                              <th>따라쓰기</th>
                              <th>스펠학습</th>
                              <th>구간테스트</th>
                              <th>전체 시험</th>
                          `) : ''}
                          ${resultType === "sentence" ? (`
                              <th>스피킹</th>
                              <th>따라쓰기</th>
                              <th>영작</th>
                              <th>문장순서</th>
                              <th>구간테스트</th>
                              <th>전체 시험</th>
                          `) : ''}
                          ${resultType === "test" ? (`
                              <th>구간테스트</th>
                              <th>전체 시험</th>
                          `) : ''}
                        </tr>
                    </thead>
                    <tbody>
                        <tr>
                          
                          ${resultType === "word" ? (`
                              <td>${item.speaking_progress ?? "0.00"}%</td>
                              <td>${item.write_progress ?? "0.00"}%</td>
                              <td>${item.spell_progress ?? "0.00"}%</td>
                              <td>${item.study_test_progress ?? "0.00"}%</td>
                              <td>${item.exam_score === null || item.exam_score === "" ? "-" : item.exam_score}</td>
                          `) : ''}
                          ${resultType === "sentence" ? (`
                              <td>${item.speaking_progress ?? "0.00"}%</td>
                              <td>${item.write_progress ?? "0.00"}%</td>
                              <td>${item.composit_progress ?? "0.00"}%</td>
                              <td>${item.sort_progress ?? "0.00"}%</td>
                              <td>${item.study_test_progress ?? "0.00"}%</td>
                              <td>${item.exam_score === null || item.exam_score === "" ? "-" : item.exam_score}</td>
                          `) : ''}
                          ${resultType === "test" ? (`
                              <td>${item.study_test_progress ?? "0.00"}%</td>
                              <td>${item.exam_score === null || item.exam_score === "" ? "-" : item.exam_score}</td>
                          `) : ''}
                        </tr>

                    </tbody>
                </table>
                <div class="chart-container">
                ${isChartData ? (
                    `<canvas id="chart-${index}"></canvas>`
                    ) : ''}
                </div>
                <div>
                  ${(resultType === "word" || resultType === "sentence" || resultType === "test") ? (`
                    <p>총 ${totalSteps}차시 중 ${progressRate}% 진행중</p>      
                    `) : ''}
                </div>
                <div style={{ height: '20px', width: '100%' }}>&nbsp</div>
                `) : ''}
  
            </section>`;
        })
        .join('')}

    <div style={{ height: '40px', width: '100%' }}>&nbsp</div>
      <!-- 총계 테이블 추가 -->

      <div class="total-label">
          총괄 합계
      </div>

      <table class="styled-table">
        <thead>
          <tr>
            <th>단어</th>
            <th>문장</th>
            <th>일반문제</th>
            <th>리스닝</th>
            <th>강의</th>
            <th>소리영어</th>
            <th>원서</th>

          </tr>
        </thead>
        <tbody>
        
          <tr>
                <td>${filteredData
        .filter(item => item.result_type === 'word')
        .reduce((total, item) => {
          const studentSteps = item.test_result?.student_word_steps ?? [];
          const stepCount = item.test_result?.word_step_count ?? [];

          return total + studentSteps.reduce((acc, studentStep) => {
            const matchedStep = stepCount.find(step => step.word_step_id === studentStep.word_step_id);
            return acc + (matchedStep?.word_step_pool_count || 0);
          }, 0);
        }, 0)}
                </td>

                <td>${filteredData
        .filter(item => item.result_type === 'sentence')
        .reduce((total, item) => {

          const studentSteps = item.test_result?.student_sentence_steps ?? [];
          const stepCount = item.test_result?.sentence_step_count ?? [];

          return total + studentSteps.reduce((acc, studentStep) => {
            const matchedStep = stepCount.find(step => step.sentence_step_id === studentStep.sentence_step_id);
            return acc + (matchedStep?.sentence_step_pool_count || 0);
          }, 0);
        }, 0)}
                </td>

                <td>${filteredData
        .filter(item => item.result_type === 'test')
        .reduce((total, item) => {

          const studentSteps = item.test_result?.student_test_steps ?? [];
          const stepCount = item.test_result?.test_step_count ?? [];

          return total + studentSteps.reduce((acc, studentStep) => {
            if (studentStep.test_step_type === "TEXT_GRAMMAR") {
              const matchedStep = stepCount.find(step => step.test_step_id === studentStep.test_step_id);
              return acc + (matchedStep?.test_step_item_count || 0);
            }
            return acc;
          }, 0);
        }, 0)}
                </td> <!-- "구문정독" "일반문제" 값 가져오기 -->
                
                <td>${filteredData
        .filter(item => item.result_type === 'test')
        .reduce((total, item) => {
          const studentSteps = item.test_result?.student_test_steps ?? [];
          const stepCount = item.test_result?.test_step_count ?? [];

          return total + studentSteps.reduce((acc, studentStep) => {
            if (studentStep.test_step_type === "LISTEN") {
              const matchedStep = stepCount.find(step => step.test_step_id === studentStep.test_step_id);
              return acc + (matchedStep?.test_step_item_count || 0);
            }
            return acc;
          }, 0);
        }, 0)}
                </td> <!-- "리스닝" 값 가져오기 -->

                <td>${filteredData
        .filter(item => item.result_type === 'topic' && item.study_test_progress !== "1")  // 강의 조건 적용
        .length                                             // 개수 반환
      }</td> <!-- "강의" 값 가져오기 -->

                <td>${filteredData
        .filter(item => item.result_type === 'topic' && item.study_test_progress === "1") // 소리영어 조건 적용
        .length                                             // 개수 반환
      }</td> <!-- "소리영어" 값 가져오기 -->

                <td>${filteredData
        .filter(item => item.result_type === 'ebook')       // result_type이 'ebook'인 항목만 선택
        .length                                             // "ebook" 항목 개수를 반환
      }</td> <!-- "원서" 값 가져오기 -->
              </tr>
        </tbody>
      </table>

    </main>

    <!-- Draw graph -->
    <script>
        ${filteredData
        .map((item, index) => {

          const chartData = processChartData(item.test_result, item.result_type);
          if (!chartData) return ""; // 무시 처리

          return `
            const ctx${index} = document.getElementById('chart-${index}').getContext('2d');
            new Chart(ctx${index}, {
                type: 'bar',
                data: ${JSON.stringify(chartData)},
                options: {
                    responsive: true,
                    maintainAspectRatio: false,
                    scales: {
                        y: {
                            beginAtZero: true
                        }
                    }
                }
            });`;
        })
        .join('')}
    </script>
</body>
</html>`;
  };

  // HTML 전송
  const sendHTMLToServer = async (bodyData) => {
    if (selectedIndexes.length === 0) {
      alert('출력할 항목을 선택하세요');
      return null;
    }

    // ### 성적표 PDF변환 ###
    // endpoint  : /report/api/getData/generate-report
    // method : post
    // body : { studentId, layoutType:99, htmlContent }    

    const htmlTemplate = createHTMLTemplate();

    const endpoint = `/report/api/getData/generate-report`; // 가상의 서버 주소

    const requestBody1 = {
      ...bodyData,
      htmlContent: htmlTemplate,
    };

    try {
      const response = await axios.post(endpoint,
        requestBody1,
        {
          withCredentials: true, // 쿠키 포함 옵션
          headers: {
            "Content-Type": "application/json",
            'Authorization': `Bearer ${bearerToken}`,
            'x-csrf-token': `${_csrf}`,
          },
        });

      if (response) {
        if (response.data.result) {
          return (response.data.data); // URL 반환 (Side에서 사용)
        } else {
          // ('==> 실패 1 ');
          return null;
        }
      } else {
        // ('==> 실패 2 ');
        return null;
      }
    } catch (error) {
      console.error("HTML 전송 중 오류 발생:", error);
      return null;
    }
  };

  // 사용 예시
  // const lastDate = "2025-02-20T13:57:42.396Z";
  // (formatToKoreanTime(lastDate)); // 출력: "2025/02/20 22:57"
  function formatToKoreanTime(dateString) {

    if (!dateString) {
      console.error("유효하지 않은 날짜입니다:", dateString);
      return '00/00 00:00';
    }

    const date = new Date(dateString);
    if (isNaN(date.getTime())) { // 날짜가 잘못된 경우
      console.error("날짜 포맷이 잘못되었습니다:", dateString);
      return '00/00 00:00';
    }

    const formatter = new Intl.DateTimeFormat('ko-KR', {
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
      timeZone: 'Asia/Seoul'
    });

    const parts = formatter.formatToParts(date);

    const month = parts.find(p => p.type === 'month').value;
    const day = parts.find(p => p.type === 'day').value;
    const hour = parts.find(p => p.type === 'hour').value;
    const minute = parts.find(p => p.type === 'minute').value;

    return `${month}/${day} ${hour}:${minute}`;
  }

  const getLatestDate = (item) => {
    const dates = [];

    if (item.exam_date) dates.push(new Date(item.exam_date));
    if (item.last_date) dates.push(new Date(item.last_date));

    const resultType = item.result_type;
    const studentStepsKey = resultType === "word" ? "student_word_steps"
      : resultType === "sentence" ? "student_sentence_steps"
        : resultType === "test" ? "student_test_steps"
          : null;

    // test_result가 존재하는지 먼저 확인하여 에러 방지
    if (studentStepsKey && item.test_result?.[studentStepsKey]) {
      item.test_result[studentStepsKey].forEach(step => {
        if (step?.step_test_date) {  // step이 존재하는지도 확인
          dates.push(new Date(step.step_test_date));
        }
      });
    }

    if (dates.length === 0) return null;

    return new Date(Math.max(...dates.map(date => date.getTime())));
  };

  // 차트 생성 및 업데이트
  useEffect(() => {

    if (data) {

      data.forEach((item, index) => {
        const chartRef = chartRefs.current[index];
        if (!chartRef) {
          return;
        }

        // 기존 차트가 존재하면 파괴
        if (chartInstances.current[index]) {
          chartInstances.current[index].destroy();
        }

        // 새로운 차트 생성
        const chartData = processChartData(item.test_result, item.result_type);
        if (!chartData) return; // 무시 처리

        chartInstances.current[index] = new Chart(chartRef, {
          type: 'bar',
          data: chartData,
          options: {
            responsive: true,
            maintainAspectRatio: false,
            scales: {
              y: {
                beginAtZero: true,
              },
            },
          },
        });
      });

      // Cleanup 함수로 모든 차트 정리
      return () => {
        // eslint-disable-next-line react-hooks/exhaustive-deps
        chartInstances.current.forEach((chart) => chart?.destroy());
      };

    } else {
      // ('==> No Report Data : no draw')
    }
  }, [data]);

  const getCompletedPoolCountByType = (resultType) => {
    let stepCountKey, studentStepsKey, idKey, poolCountKey;

    switch (resultType) {
      case 'word':
        stepCountKey = 'word_step_count';
        studentStepsKey = 'student_word_steps';
        idKey = 'word_step_id';
        poolCountKey = 'word_step_pool_count';
        break;

      case 'sentence':
        stepCountKey = 'sentence_step_count';
        studentStepsKey = 'student_sentence_steps';
        idKey = 'sentence_step_id';
        poolCountKey = 'sentence_step_pool_count';
        break;

      case 'test':
        stepCountKey = 'test_step_count';
        studentStepsKey = 'student_test_steps';
        idKey = 'test_step_id';
        poolCountKey = 'test_step_item_count';
        break;

      default:
        return { total: 0, listening: 0, grammar: 0 };
    }

    return selectedIndexes?.reduce(
      (totals, selectedIndex) => {
        const item = data[selectedIndex];

        if (!item || item.result_type !== resultType) return totals;

        // test_result가 null이면 빈 배열 반환
        const stepCount = item.test_result?.[stepCountKey] ?? [];
        const studentSteps = item.test_result?.[studentStepsKey] ?? [];

        if (!stepCount.length || !studentSteps.length) return totals;

        studentSteps.forEach((studentStep) => {
          const matchedStep = stepCount.find(step => step[idKey] === studentStep[idKey]);

          if (matchedStep) {
            const poolCount = matchedStep[poolCountKey] || 0;
            totals.total += poolCount;

            if (resultType === "test") {
              // LISTEN (리스닝)인 경우
              if (studentStep.test_step_type === "LISTEN") {
                totals.listening += poolCount;
              }
              // TEXT_GRAMMAR (구문정독)인 경우
              if (studentStep.test_step_type === "TEXT_GRAMMAR") {
                totals.grammar += poolCount;
              }
            }
          }
        });

        return totals;
      },
      { total: 0, listening: 0, grammar: 0 } // 총합, 리스닝, 구문정독을 포함한 객체 반환
    );
  };

  // item 갯수만 확인한 것
  const getCompletedPoolCountByTypeNew = (resultType) => {
    if (resultType !== 'topic') return { lecture: 0, soundEnglish: 0 };

    // 선택된 항목들 중 result_type이 'topic'인 데이터만 필터링
    const selectedTopics = selectedIndexes
      .map(idx => data[idx])
      .filter(item => item?.result_type === 'topic');

    // 강의(study_test_progress === 1)와 소리영어(study_test_progress !== 1) 개수 계산
    const lectureCount = selectedTopics.filter(item => item.study_test_progress !== "1").length;
    const soundEnglishCount = selectedTopics.filter(item => item.study_test_progress === "1").length;

    return {
      lecture: lectureCount,
      soundEnglish: soundEnglishCount
    };
  };

  // 해당하는 item 의 갯수
  const getCompletedPoolCountByTypeEbook = () => {
    return selectedIndexes.reduce((total, selectedIndex) => {
      const item = data[selectedIndex];

      if (!item || item.result_type !== "ebook") return total;

      return total + 1; // "result_type": "ebook"인 항목의 개수를 누적
    }, 0);
  };


  return (

    // <div>
    <div className={style.rcReportCardContainer}>

      {/* 학습기간 정보 추가 */}
      <div className={style.rcStudyPeriod} style={{ marginBottom: "16px", fontWeight: "bold", fontSize: "16px" }}>
        학습기간: {startDate} ~ {endDate}
        <br />
      </div>
      <div >
        <div className={style.rcReportSectionContainer}>
          {data?.length > 0 &&
            data?.map((item, index) => {

              const chartData = processChartData(item.test_result, item.result_type);
              if (!chartData) {
                // item.test_result === null 이면, 테이블만 표시한다.
                // if (item.result_type === "word" || item.result_type === "sentence" || item.result_type === "test") {
                //   // return null; // 무시 처리
                // }
              }

              const resultType = item.result_type;  // "word", "sentence", "test"

              let totalItemCount = 0, totalSteps = 0, completedSteps = 0, progressRate = 0;
              if (resultType === "word" || resultType === "sentence" || resultType === "test") {
                const stepCountKey = `${resultType}_step_count`;
                const studentStepsKey = `student_${resultType}_steps`;

                // result_type === "test"인 경우 키가 다름
                const poolCountKey = resultType === "test" ? "test_step_item_count" : `${resultType}_step_pool_count`;

                if (item.test_result) {
                  // 총 항목 개수 계산
                  totalItemCount = item.test_result[stepCountKey]?.reduce(
                    (acc, step) => acc + (step[poolCountKey] || 0), 0
                  ) || 0;

                  // 총 차시 개수 계산
                  totalSteps = item.test_result[stepCountKey]?.length || 0;

                  // 진행 차시 개수 계산
                  completedSteps = item.test_result[studentStepsKey]?.length || 0;

                  // 진행율(%) 계산 (소수점 반올림)
                  progressRate = totalSteps > 0 ? Math.round((completedSteps / totalSteps) * 100) : 0;
                }
                // =========================
              }

              const typeLabel = resultType === "word" ? "단어"
                : resultType === "sentence" ? "문장"
                  : resultType === "test" ? "문제"
                    : resultType === "topic" ? "권"
                      : resultType === "ebook" ? "권"
                        : "";

              const typeLabel2 = resultType === "word" ? "단어"
                : resultType === "sentence" ? "문장"
                  : resultType === "test" ? "문제집"
                    : resultType === "topic" ? "토픽"
                      : resultType === "ebook" ? "Ebook"
                        : "";

              const latestDate = getLatestDate(item);
              const formattedLatestDate = latestDate ? formatToKoreanTime(latestDate) : "00/00 00:00";

              return (
                <div className={style.rcReportSectionCard} key={index}>
                  <div
                    key={index}
                  // className={style.rcReportSection}
                  >
                    <input
                      type="checkbox"
                      checked={selectedIndexes.includes(index)}
                      onChange={() => toggleSelection(index)}
                    />
                    <div className={style.rcHeaderLabelContainer}>
                      <h2><span className={style.rcHeaderLabel}>{typeLabel2}</span><span className={style.rcHeaderInfoTwo}>{item.title}</span></h2>
                    </div>

                    {(resultType === "word" || resultType === "sentence" || resultType === "test") && (
                      <>
                        <p> <b>총 항목 갯수:</b> {totalItemCount} {typeLabel}</p>
                        <p> <b>진행율:</b> 총 {totalSteps}차시 중 {progressRate}% 진행중</p>
                      </>
                    )}

                    <p> <b>일시:</b>{formattedLatestDate}</p>

                    <table className={style.rcStyledTable}>
                      <thead>
                        <tr>
                          {/* resultType별로 테이블 컬럼 변경 */}
                          {resultType === "word" && (
                            <>
                              <th>스피킹</th>
                              <th>따라쓰기</th>
                              <th>스펠학습</th>
                              <th>구간테스트</th>
                              <th>전체 시험</th>
                            </>
                          )}
                          {resultType === "sentence" && (
                            <>
                              <th>스피킹</th>
                              <th>따라쓰기</th>
                              <th>영작</th>
                              <th>문장순서</th>
                              <th>구간테스트</th>
                              <th>전체 시험</th>
                            </>
                          )}
                          {resultType === "test" && (
                            <>
                              <th>구간테스트</th>
                              <th>전체 시험</th>
                            </>
                          )}
                        </tr>
                      </thead>
                      <tbody>
                        <tr>
                          {/* resultType별로 해당하는 데이터만 표시 */}
                          {resultType === "word" && (
                            <>
                              <td>{item.speaking_progress ?? "0.00"}%</td>
                              <td>{item.write_progress ?? "0.00"}%</td>
                              <td>{item.spell_progress ?? "0.00"}%</td>
                              <td>{item.study_test_progress ?? "0.00"}%</td>
                              <td>{item.exam_score === null || item.exam_score === "" ? "-" : item.exam_score}</td>
                            </>
                          )}
                          {resultType === "sentence" && (
                            <>
                              <td>{item.speaking_progress ?? "0.00"}%</td>
                              <td>{item.write_progress ?? "0.00"}%</td>
                              <td>{item.composit_progress ?? "0.00"}%</td>
                              <td>{item.sort_progress ?? "0.00"}%</td>
                              <td>{item.study_test_progress ?? "0.00"}%</td>
                              <td>{item.exam_score === null || item.exam_score === "" ? "-" : item.exam_score}</td>
                            </>
                          )}
                          {resultType === "test" && (
                            <>
                              <td>{item.study_test_progress ?? "0.00"}%</td>
                              <td>{item.exam_score === null || item.exam_score === "" ? "-" : item.exam_score}</td>
                            </>
                          )}
                        </tr>
                      </tbody>
                    </table>

                    {(resultType === "word" || resultType === "sentence" || resultType === "test") && (item.test_result !== null) && (
                      <div className={style.rcChartContainer}>
                        <canvas
                          ref={(el) => (chartRefs.current[index] = el)}
                          id={`chart-${index}`}
                        ></canvas>
                      </div>
                    )}

                  </div>
                </div>
              )
            }
            )}

          {/* 전체 합계 표시 테이블 추가 */}
          {data?.length > 0 && (
            <div className={style.rcTotalSummaryContainer}>
              {/* <table className={`${style.rcStyledTable} ${style.rcTotalSummaryTable}`}> */}
              <table className={`${style.rcStyledTable}`}>
                {/* <table className={`${style.rcTotalSummaryTable}`}> */}
                <thead>
                  <tr>
                    <th>단어</th>
                    <th>문장</th>
                    <th>일반문제</th>
                    <th>리스닝</th>
                    <th>강의</th>
                    <th>소리영어</th>
                    <th>원서</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td>{getCompletedPoolCountByType("word").total}</td>
                    <td>{getCompletedPoolCountByType("sentence").total}</td>
                    <td>{getCompletedPoolCountByType("test").grammar}</td>
                    <td>{getCompletedPoolCountByType("test").listening}</td>
                    <td>{getCompletedPoolCountByTypeNew("topic").lecture}</td>
                    <td>{getCompletedPoolCountByTypeNew("topic").soundEnglish}</td>
                    <td>{getCompletedPoolCountByTypeEbook()}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          )}

        </div>
      </div>
    </div>
  );
});

export default ReportCard;